import { WidgetBranding } from '../types';

/**
 * Escapes HTML special characters to prevent XSS when rendering tenant-provided text.
 */
export function escapeHTML(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

// --- CSS Sanitization ---

const MAX_CSS_LENGTH = 10240; // 10KB cap

const DANGEROUS_CSS_PATTERNS: RegExp[] = [
  /expression\s*\(/i,           // IE CSS expressions
  /javascript\s*:/i,            // javascript: in url()
  /behavior\s*:/i,              // IE behavior property
  /-moz-binding\s*:/i,          // Firefox XBL binding
  /<\/style/i,                  // Style tag breakout
  /<script/i,                   // Script injection
  /\\00/,                       // Null byte injection
  /@import/i,                   // External stylesheet import
];

// Allow url() only for Google Fonts domains
const ALLOWED_URL_DOMAINS = ['fonts.googleapis.com', 'fonts.gstatic.com'];

function isAllowedURL(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ALLOWED_URL_DOMAINS.some(domain => parsed.hostname === domain);
  } catch {
    // data: URIs for small inline resources are OK, everything else blocked
    return url.startsWith('data:');
  }
}

/**
 * Validates custom CSS for dangerous patterns, size limits, and URL restrictions.
 * Returns { valid: true } if CSS is safe to inject, or { valid: false, error: string } if not.
 */
export function sanitizeCSS(css: string): { valid: boolean; error?: string } {
  if (css.length > MAX_CSS_LENGTH) {
    return { valid: false, error: `Custom CSS exceeds maximum size of ${MAX_CSS_LENGTH} bytes` };
  }

  for (const pattern of DANGEROUS_CSS_PATTERNS) {
    if (pattern.test(css)) {
      return { valid: false, error: `Custom CSS contains a blocked pattern: ${pattern.source}` };
    }
  }

  // Check all url() values
  const urlRegex = /url\s*\(\s*(['"]?)(.*?)\1\s*\)/gi;
  let urlMatch;
  while ((urlMatch = urlRegex.exec(css)) !== null) {
    const urlValue = urlMatch[2].trim();
    if (!isAllowedURL(urlValue)) {
      return { valid: false, error: 'Custom CSS contains a blocked URL: only Google Fonts URLs and data: URIs are allowed' };
    }
  }

  return { valid: true };
}

// --- CSS Variable Validation ---

function validateCSSVariableKey(key: string): boolean {
  return /^--[a-zA-Z0-9-_]+$/.test(key);
}

function validateCSSVariableValue(value: string): boolean {
  return !DANGEROUS_CSS_PATTERNS.some(pattern => pattern.test(value));
}

// Color property mappings for branding
const COLOR_PROPERTIES: Record<string, string> = {
  primaryColor: '--widget-primary-color',
  secondaryColor: '--widget-secondary-color',
  accentColor: '--widget-accent-color',
  textColor: '--widget-text-color',
  backgroundColor: '--widget-background-color',
  surfaceColor: '--widget-surface-color',
  successColor: '--widget-success-color',
  warningColor: '--widget-warning-color',
  errorColor: '--widget-error-color'
};

// Font size scale configurations
const FONT_SIZE_SCALES: Record<string, Record<string, string>> = {
  small: {
    '--widget-font-size-xs': '10px',
    '--widget-font-size-sm': '12px',
    '--widget-font-size-base': '13px',
    '--widget-font-size-lg': '14px',
    '--widget-font-size-xl': '16px',
    '--widget-font-size-2xl': '18px',
    '--widget-font-size-3xl': '20px',
    '--widget-font-size-4xl': '24px',
  },
  medium: {
    '--widget-font-size-xs': '11px',
    '--widget-font-size-sm': '13px',
    '--widget-font-size-base': '14px',
    '--widget-font-size-lg': '16px',
    '--widget-font-size-xl': '18px',
    '--widget-font-size-2xl': '20px',
    '--widget-font-size-3xl': '24px',
    '--widget-font-size-4xl': '28px',
  },
  large: {
    '--widget-font-size-xs': '12px',
    '--widget-font-size-sm': '14px',
    '--widget-font-size-base': '16px',
    '--widget-font-size-lg': '18px',
    '--widget-font-size-xl': '20px',
    '--widget-font-size-2xl': '24px',
    '--widget-font-size-3xl': '28px',
    '--widget-font-size-4xl': '32px',
  }
};

const LINE_HEIGHT_MAP: Record<string, string> = {
  tight: '1.25',
  normal: '1.5',
  relaxed: '1.75'
};

const BORDER_RADIUS_MAP: Record<string, string> = {
  none: '0',
  small: '4px',
  medium: '8px',
  large: '12px',
  full: '9999px'
};

const SHADOW_MAP: Record<string, string> = {
  none: 'none',
  small: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  medium: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  large: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
};

function setProperties(root: HTMLElement, properties: Record<string, string>): void {
  Object.entries(properties).forEach(([prop, value]) => {
    root.style.setProperty(prop, value);
  });
}

/**
 * Applies branding configuration to the widget by injecting CSS custom properties
 */
export function applyBranding(branding?: WidgetBranding, containerId?: string): void {
  if (!branding) return;

  const root = containerId
    ? document.querySelector(`#${containerId}`) as HTMLElement
    : document.documentElement;

  if (!root) return;

  // Apply color properties
  Object.entries(COLOR_PROPERTIES).forEach(([brandingKey, cssProperty]) => {
    const value = branding[brandingKey as keyof WidgetBranding];
    if (typeof value === 'string') {
      root.style.setProperty(cssProperty, value);
    }
  });

  // Set short aliases for CSS file compatibility
  if (branding.primaryColor) {
    root.style.setProperty('--primary-color', branding.primaryColor);
    root.style.setProperty('--primary-hover', branding.primaryColor);
  }
  if (branding.accentColor) {
    root.style.setProperty('--accent-color', branding.accentColor);
  }
  if (branding.surfaceColor) {
    root.style.setProperty('--surface-color', branding.surfaceColor);
  }

  // Typography
  if (branding.fontFamily) {
    root.style.setProperty('--widget-font-family', branding.fontFamily);
  }

  if (branding.fontSize && FONT_SIZE_SCALES[branding.fontSize]) {
    setProperties(root, FONT_SIZE_SCALES[branding.fontSize]);
  }

  if (branding.lineHeight && LINE_HEIGHT_MAP[branding.lineHeight]) {
    root.style.setProperty('--widget-line-height-normal', LINE_HEIGHT_MAP[branding.lineHeight]);
  }

  // Border Radius
  if (branding.borderRadius && BORDER_RADIUS_MAP[branding.borderRadius]) {
    const radius = BORDER_RADIUS_MAP[branding.borderRadius];
    setProperties(root, {
      '--widget-radius-md': radius,
      '--widget-radius-lg': `calc(${radius} * 1.5)`,
      '--widget-radius-xl': `calc(${radius} * 2)`
    });
  }

  // Shadow Level
  if (branding.shadowLevel && SHADOW_MAP[branding.shadowLevel]) {
    root.style.setProperty('--widget-shadow-lg', SHADOW_MAP[branding.shadowLevel]);
  }

  // Custom CSS Variables (validated)
  if (branding.cssVariables) {
    const safeVars: Record<string, string> = {};
    Object.entries(branding.cssVariables).forEach(([key, value]) => {
      if (validateCSSVariableKey(key) && validateCSSVariableValue(value)) {
        safeVars[key] = value;
      } else {
        console.warn(`[Branding] Blocked invalid CSS variable: ${key}`);
      }
    });
    setProperties(root, safeVars);
  }
}

/**
 * Generates a branded widget header with logo and company name
 */
export function createBrandedHeader(branding?: WidgetBranding): string {
  if (!branding?.logoUrl && !branding?.companyName) {
    return '';
  }

  let headerHTML = '<div class="widget-branded-header">';

  if (branding.logoUrl) {
    // Validate logoUrl is a proper URL (http/https only)
    const isValidUrl = /^https?:\/\//i.test(branding.logoUrl);
    if (isValidUrl) {
      const escapedUrl = escapeHTML(branding.logoUrl);
      const escapedAlt = escapeHTML(branding.companyName || 'Logo');
      headerHTML += `<img src="${escapedUrl}" alt="${escapedAlt}" class="widget-logo" />`;
    }
  }

  if (branding.companyName) {
    headerHTML += `<span class="widget-company-name">${escapeHTML(branding.companyName)}</span>`;
  }

  headerHTML += '</div>';

  return headerHTML;
}

/**
 * Creates powered by text for widget footer
 */
export function createPoweredByText(branding?: WidgetBranding): string {
  if (branding?.showPoweredBy === false) {
    return '';
  }

  const rawText = branding?.poweredByText || 'Powered by AI Booking Engine';
  // Enforce max length (100 chars) and escape HTML
  const truncated = rawText.slice(0, 100);
  return escapeHTML(truncated);
}

/**
 * Injects custom CSS if provided
 */
export function injectCustomCSS(customCSS: string, widgetId: string): void {
  const existingStyle = document.querySelector(`#${widgetId}-custom-css`);
  if (existingStyle) {
    existingStyle.remove();
  }

  const result = sanitizeCSS(customCSS);
  if (!result.valid) {
    console.warn(`[Branding] Custom CSS rejected: ${result.error}`);
    return;
  }

  const styleElement = document.createElement('style');
  styleElement.id = `${widgetId}-custom-css`;
  styleElement.textContent = customCSS;
  document.head.appendChild(styleElement);
}